/**
 * CYP ASD History — Section configuration and pre-filled dummy data.
 *
 * Mirrors the triage proforma workflow from the Referral Management System,
 * presenting the same categories of information (developmental history,
 * family/home life, medical history, etc.) in a read-only, pre-filled
 * format suitable for the assessment context.
 *
 * Each section has:
 *   - id / title / shortTitle for sidebar navigation
 *   - isDual: if true, shows two columns (School + Parent/Carer)
 *   - questions: array of line items, each with:
 *       { id, label, answer }           — single-respondent
 *       { id, label, schoolAnswer, parentAnswer } — dual-respondent
 *
 * Formatting conventions in answer strings:
 *   • Lines starting with "• " render as indented bullet points
 *   • Newlines (\n) create line breaks between items
 *
 * The dummy data represents "Alex M.", a 9-year-old male referred for
 * social communication difficulties, rigid behaviours, and sensory
 * sensitivities — consistent with seed data across all CYP ASD modules.
 */

const HISTORY_SECTIONS = [
  {
    id: 'referral',
    title: 'Referral Overview',
    shortTitle: 'Referral Overview',
    isDual: true,
    questions: [
      {
        id: 'referrer',
        label: 'Referred by',
        schoolAnswer: 'School SENCO (Mrs. K. Patel)\n• Social communication concerns\n• Peer relationship difficulties\n• Rigid behaviours in classroom setting',
        parentAnswer: 'Community paediatrician (Dr. R. Hussain)\n• Longstanding parental concerns since age 3',
      },
      {
        id: 'referralReason',
        label: 'Main concerns',
        schoolAnswer: '• Difficulty following unstructured time\n• Limited reciprocal play with peers\n• Becomes distressed with changes to routine\n• Very focused on specific topics (Minecraft, trains)\n• Literal interpretation of instructions',
        parentAnswer: '• Always been "different" — limited imaginative play as a toddler\n• Prefers adult company\n• Very particular about food and clothing\n• Meltdowns when plans change\n• Takes things very literally\n• Struggles to make and keep friends',
      },
      {
        id: 'previousAssessments',
        label: 'Previous assessments',
        schoolAnswer: '• Educational psychology (age 7) — average cognitive ability, recommended classroom adaptations\n• No prior ASD assessment',
        parentAnswer: '• Speech and language therapy (age 5) — mild pragmatic language difficulties noted\n• GP considered CAMHS referral for anxiety but deferred pending ASD assessment',
      },
      {
        id: 'strengths',
        label: 'Strengths identified',
        schoolAnswer: '• Excellent factual knowledge\n• Strong maths skills (particularly mental arithmetic)\n• Follows rules reliably\n• Honest and kind-hearted',
        parentAnswer: '• Very affectionate with family on his own terms\n• Incredible memory\n• Passionate about his interests\n• Good sense of humour with familiar people',
      },
    ],
  },
  {
    id: 'developmental',
    title: 'Developmental History',
    shortTitle: 'Developmental History',
    isDual: false,
    questions: [
      {
        id: 'pregnancy',
        label: 'Pregnancy and birth',
        answer: '• Uncomplicated pregnancy\n• Born at 39+2 weeks via spontaneous vaginal delivery\n• Birth weight 3.4kg\n• No neonatal concerns\n• APGAR 9/9',
      },
      {
        id: 'motorMilestones',
        label: 'Motor milestones',
        answer: '• Sat independently at 6 months\n• Crawled at 9 months\n• Walked at 13 months\n• Fine motor age-appropriate — pincer grip by 10 months',
      },
      {
        id: 'speechMilestones',
        label: 'Speech and language milestones',
        answer: '• First words at 14 months\n• Phrases by 24 months\n• Speech was "scripted" — would repeat phrases from TV verbatim\n• Pronoun reversal until age 4\n• Vocabulary advanced but pragmatic use limited',
      },
      {
        id: 'socialMilestones',
        label: 'Early social development',
        answer: '• Limited pointing to share (mostly to request)\n• Did not bring objects to show parents\n• Inconsistent response to name by 18 months\n• Preferred to play alone — would line up cars for extended periods\n• Did not engage in pretend play spontaneously',
      },
      {
        id: 'feeding',
        label: 'Feeding',
        answer: '• Breastfed for 4 months, then formula\n• Weaning was difficult — very limited range of accepted textures and colours\n• Still a restrictive eater (beige foods only — toast, chips, chicken nuggets, plain pasta)\n• Will not eat foods that touch each other on the plate',
      },
      {
        id: 'sleep',
        label: 'Sleep',
        answer: '• Has always needed a strict bedtime routine (same order: bath, story, lights off)\n• Difficulty settling if routine disrupted\n• Currently takes 30–45 mins to fall asleep\n• Wakes 1–2x/week — difficulty returning to sleep without parental presence',
      },
      {
        id: 'toileting',
        label: 'Toileting',
        answer: '• Daytime continent by 3.5 years (slightly delayed)\n• Nighttime dry by 5 years\n• No current concerns',
      },
    ],
  },
  {
    id: 'family',
    title: 'Family and Home Life',
    shortTitle: 'Family & Home',
    isDual: false,
    questions: [
      {
        id: 'familyStructure',
        label: 'Family structure',
        answer: '• Lives with both parents (mum — teacher, dad — software engineer) and younger sister (age 6)\n• Maternal grandparents nearby and involved in care',
      },
      {
        id: 'familyRelationships',
        label: 'Family relationships',
        answer: '• Close relationship with parents, especially dad (shared interest in computers)\n• Relationship with sister is variable — becomes frustrated when she disrupts his things or enters his room\n• Generally affectionate but on his own terms',
      },
      {
        id: 'homeRoutine',
        label: 'Home routine',
        answer: '• Very dependent on routine — same breakfast every day, specific order for getting ready\n• Becomes highly distressed if the morning routine is disrupted (e.g. running late)\n• Parents have learned to give advance warning of any changes',
      },
      {
        id: 'socialLife',
        label: 'Social life outside school',
        answer: '• Attends a Minecraft club (online) — engages well in this structured, interest-based context\n• Has one friend from school who visits occasionally — play is usually parallel (both on separate devices)\n• Does not attend birthday parties (overwhelmed by noise/unpredictability)',
      },
      {
        id: 'dailyLiving',
        label: 'Daily living skills',
        answer: '• Can dress independently but insists on soft fabrics\n• Will not wear jeans, rough-textured school trousers, or socks with seams\n• Brushes teeth but needs prompting for sequencing\n• Can make simple snacks',
      },
      {
        id: 'screenTime',
        label: 'Screen time and interests',
        answer: '• Very focused on Minecraft and train videos (YouTube)\n• Can spend 3–4 hours if not redirected\n• Becomes distressed when screen time ends\n• Has encyclopaedic knowledge of train models and Minecraft mechanics',
      },
    ],
  },
  {
    id: 'education',
    title: 'Education History',
    shortTitle: 'Education',
    isDual: true,
    questions: [
      {
        id: 'currentSchool',
        label: 'Current school / year',
        schoolAnswer: '• Year 4 at local mainstream primary school\n• Has been at the same school since Reception',
        parentAnswer: '• Same school since age 4\n• Generally settled but anxious about transition to secondary school',
      },
      {
        id: 'academicProgress',
        label: 'Academic progress',
        schoolAnswer: '• Maths: Working at expected level (strength — particularly mental arithmetic)\n• Reading: Accuracy good but comprehension of inference/character motivation is weak\n• Writing: Below expected — finds open-ended tasks very challenging',
        parentAnswer: '• Homework is a major source of stress — particularly creative writing\n• Enjoys maths homework\n• Reading at home limited to non-fiction (train books, Minecraft guides)',
      },
      {
        id: 'classroomBehaviour',
        label: 'Behaviour in school',
        schoolAnswer: '• Compliant and rule-following in structured lessons\n• Struggles during group work — insists on doing things his way, does not compromise\n• Becomes anxious before assemblies and PE (unpredictable elements)\n• Can appear "switched off" during carpet time',
        parentAnswer: '• Comes home exhausted most days\n• Mum describes him as "holding it together" at school then "falling apart" at home\n• Meltdowns after school 3–4x/week',
      },
      {
        id: 'peerRelationships',
        label: 'Peer relationships',
        schoolAnswer: '• Tends to be on the periphery of social groups\n• Will join a game if it has clear rules (football) but leaves if rules are changed or disputes arise\n• Has been a target of low-level teasing ("he\'s weird") which he doesn\'t always recognise',
        parentAnswer: '• Has one friend (also interested in Minecraft)\n• Rarely invited to play dates or parties\n• Mum finds this upsetting but Alex appears less concerned — says he "doesn\'t mind" being alone',
      },
      {
        id: 'supportInPlace',
        label: 'Support in place',
        schoolAnswer: '• Visual timetable, now/next board\n• Access to a quiet space at break times\n• 1:1 TA support during transitions and unstructured time\n• Social skills group (weekly, 30 mins)\n• No EHCP — school considering application',
        parentAnswer: '• Parents attended a parenting course for children with additional needs (helpful)\n• Use visual schedules at home\n• Considering private OT for sensory difficulties',
      },
      {
        id: 'attendance',
        label: 'Attendance and exclusions',
        schoolAnswer: '• Attendance 91% — absences mainly anxiety-related (e.g. school trips, non-uniform days)\n• No formal exclusions\n• One incident of hitting a peer (aged 7) when routine was unexpectedly changed',
        parentAnswer: '• Some school refusal mornings — stomach aches and crying\n• Particularly bad on days with unexpected changes or substitute teachers',
      },
    ],
  },
  {
    id: 'medical',
    title: 'Medical History',
    shortTitle: 'Medical History',
    isDual: false,
    questions: [
      { id: 'currentHealth', label: 'Current health', answer: 'Generally well. No chronic physical health conditions.' },
      { id: 'medications', label: 'Medications', answer: '• None currently\n• Melatonin was trialled briefly at age 7 for sleep but discontinued (parents preferred behavioural approach)' },
      { id: 'hospitalisations', label: 'Hospitalisations / surgeries', answer: 'None.' },
      { id: 'hearing', label: 'Hearing', answer: 'Hearing test at age 5 — normal. No current concerns.' },
      { id: 'vision', label: 'Vision', answer: '• Wears glasses since age 6 (mild myopia)\n• Last eye test 6 months ago — stable' },
      { id: 'allergies', label: 'Allergies', answer: 'No known allergies.' },
      {
        id: 'otherMedical',
        label: 'Other medical concerns',
        answer: '• Hypermobility noted in fingers and wrists (Beighton 5/9)\n• Mild coordination difficulties — struggles with handwriting and tying shoelaces\n• Parents have not pursued OT referral',
      },
    ],
  },
  {
    id: 'psychiatric',
    title: 'Psychiatric History',
    shortTitle: 'Psychiatric History',
    isDual: false,
    questions: [
      {
        id: 'mentalHealth',
        label: 'Mental health history',
        answer: '• No formal psychiatric diagnoses\n• Parents report increasing anxiety over the past 2 years, particularly around school and social situations\n• GP considered CAMHS referral for anxiety but deferred pending ASD assessment',
      },
      {
        id: 'anxiety',
        label: 'Anxiety symptoms',
        answer: '• Worries excessively about things going wrong, changes to plans, and social situations\n• Physical symptoms: stomach aches before school (3–4x/week), difficulty sleeping night before school trips\n• Avoids new situations where possible',
      },
      {
        id: 'mood',
        label: 'Mood',
        answer: '• Generally "content" when in routine and engaged in preferred activities\n• Can become very low after social difficulties at school\n• Has said "nobody likes me" and "I wish I was normal"\n• No self-harm or suicidal ideation',
      },
      {
        id: 'behaviouralConcerns',
        label: 'Behavioural concerns',
        answer: '• Meltdowns when overwhelmed — typically shouting, crying, occasionally throwing objects\n• Parents estimate 2–3 per week\n• Usually triggered by sensory overload, unexpected changes, or social demands\n• Duration 10–30 mins\n• No aggressive behaviour outside of meltdowns',
      },
      {
        id: 'previousInterventions',
        label: 'Previous interventions',
        answer: '• School-based social skills group (ongoing)\n• Parents attended a parenting course for children with additional needs (helpful)\n• No individual therapy to date',
      },
    ],
  },
  {
    id: 'familyHistory',
    title: 'Family History',
    shortTitle: 'Family History',
    isDual: false,
    questions: [
      {
        id: 'familyNeurodevelopmental',
        label: 'Neurodevelopmental conditions in family',
        answer: '• Dad — self-identifies as "probably autistic" but never assessed; describes himself as a "geek" who struggled socially as a child\n• Maternal uncle — diagnosed ADHD as an adult\n• Paternal grandfather — described as "eccentric" and very routined',
      },
      {
        id: 'familyMentalHealth',
        label: 'Family mental health history',
        answer: '• Mum — history of generalised anxiety (managed with CBT, no medication)\n• Maternal grandmother — depression (treated)\n• No other significant psychiatric history',
      },
      {
        id: 'familyMedical',
        label: 'Family medical history',
        answer: '• Paternal grandmother — type 2 diabetes\n• No other significant family medical history\n• No genetic conditions known',
      },
    ],
  },
];

export default HISTORY_SECTIONS;
